import { useState, useCallback } from 'react';
import { APP_REGISTRY } from '../appRegistry';

export const useWindowManager = (playSound) => {
  const [windows, setWindows] = useState([]);
  const [activeWindowId, setActiveWindowId] = useState(null);
  const [highestZIndex, setHighestZIndex] = useState(10);
  const [windowCounter, setWindowCounter] = useState(0);

  const openWindow = useCallback((type, props = {}) => {
    const config = APP_REGISTRY[type];
    if (!config) return;
    playSound?.('click', 0.3);

    if (!config.multi) {
      const existing = windows.find((w) => w.type === type);
      if (existing) {
        setWindows((prev) =>
          prev.map((w) =>
            w.id === existing.id
              ? { ...w, isMinimized: false, zIndex: highestZIndex + 1 }
              : w
          )
        );
        setHighestZIndex((prev) => prev + 1);
        setActiveWindowId(existing.id);
        return;
      }
    }

    const id = config.multi ? `${type}-${windowCounter}` : type;
    const newWindow = {
      id,
      type,
      title: config.getTitle(props),
      icon: config.getIcon(),
      props,
      isMinimized: false,
      isMaximized: false,
      zIndex: highestZIndex + 1,
    };
    setWindows((prev) => [...prev, newWindow]);
    setHighestZIndex((prev) => prev + 1);
    setWindowCounter((prev) => prev + 1);
    setActiveWindowId(id);
  }, [windows, highestZIndex, windowCounter, playSound]);

  const closeWindow = useCallback((windowId) => {
    playSound?.('click', 0.2);
    setWindows((prev) => prev.filter((w) => w.id !== windowId));
    if (activeWindowId === windowId) {
      const remaining = windows.filter((w) => w.id !== windowId);
      setActiveWindowId(remaining.length > 0 ? remaining[remaining.length - 1].id : null);
    }
  }, [activeWindowId, windows, playSound]);

  const minimizeWindow = useCallback((windowId) => {
    playSound?.('click', 0.2);
    setWindows((prev) =>
      prev.map((w) => (w.id === windowId ? { ...w, isMinimized: true } : w))
    );
    if (activeWindowId === windowId) {
      const remaining = windows.filter((w) => w.id !== windowId && !w.isMinimized);
      setActiveWindowId(remaining.length > 0 ? remaining[remaining.length - 1].id : null);
    }
  }, [activeWindowId, windows, playSound]);

  const maximizeWindow = useCallback((windowId) => {
    playSound?.('click', 0.2);
    setWindows((prev) =>
      prev.map((w) => (w.id === windowId ? { ...w, isMaximized: !w.isMaximized } : w))
    );
  }, [playSound]);

  const focusWindow = useCallback((windowId) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === windowId ? { ...w, zIndex: highestZIndex + 1 } : w))
    );
    setHighestZIndex((prev) => prev + 1);
    setActiveWindowId(windowId);
  }, [highestZIndex]);

  const updateWindowTitle = useCallback((windowId, newTitle) => {
    setWindows((prev) =>
      prev.map((w) => {
        if (w.id !== windowId) return w;
        const config = APP_REGISTRY[w.type];
        const suffix = config?.getTitle({ pageTitle: '' }).replace(' - ', '').replace('Internet Exploder', '');
        return { ...w, title: `${newTitle} - Internet Exploder` };
      })
    );
  }, []);

  const handleTaskbarWindowClick = useCallback((windowId) => {
    playSound?.('click', 0.2);
    const win = windows.find((w) => w.id === windowId);
    if (!win) return;
    if (win.isMinimized) {
      setWindows((prev) =>
        prev.map((w) =>
          w.id === windowId
            ? { ...w, isMinimized: false, zIndex: highestZIndex + 1 }
            : w
        )
      );
      setHighestZIndex((prev) => prev + 1);
      setActiveWindowId(windowId);
    } else if (activeWindowId === windowId) {
      minimizeWindow(windowId);
    } else {
      focusWindow(windowId);
    }
  }, [windows, activeWindowId, highestZIndex, minimizeWindow, focusWindow, playSound]);

  return {
    windows,
    activeWindowId,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    focusWindow,
    updateWindowTitle,
    handleTaskbarWindowClick,
  };
};
